import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Alert,
  CircularProgress,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { invoicesApi, Invoice, InvoiceLineItem, UpdateInvoiceData } from '../services/invoices';
import { clientsApi, Client } from '../services/clients';
import { useCompany } from '../contexts/CompanyContext';
import { formatCurrency } from '../utils/currency';

type InvoiceFormData = {
  issueDate: string;
  dueDate: string;
  lineItems: InvoiceLineItem[];
  notes?: string;
  status: Invoice['status'];
};

const InvoiceEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [client, setClient] = useState<Client | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { baseCurrency } = useCompany();

  const { register, handleSubmit, control, watch, reset, formState: { errors } } = useForm<InvoiceFormData>({
    defaultValues: {
      issueDate: '',
      dueDate: '',
      lineItems: [],
      notes: '',
      status: 'draft',
    },
  });

  const { fields, append, remove, update } = useFieldArray({
    control,
    name: 'lineItems',
  });

  const watchedLineItems = watch('lineItems');

  useEffect(() => {
    if (id) {
      loadInvoice();
    }
  }, [id]);

  const loadInvoice = async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      setError(null);
      const invoiceData = await invoicesApi.getById(id);
      setInvoice(invoiceData);
      
      // Load client data
      const clientData = await clientsApi.getById(invoiceData.clientId);
      setClient(clientData);

      // Reset form with invoice data
      reset({
        issueDate: invoiceData.issueDate,
        dueDate: invoiceData.dueDate,
        lineItems: invoiceData.lineItems,
        notes: invoiceData.notes || '',
        status: invoiceData.status,
      });
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load invoice');
    } finally {
      setLoading(false);
    }
  };

  const calculateTotals = (items: InvoiceLineItem[]) => {
    const subtotal = items.reduce((sum, item) => {
      const amount = item.quantity * item.rate;
      return sum + amount;
    }, 0);
    // For now, tax is 0 or can be calculated based on tax config
    const tax = invoice?.tax || 0;
    const total = subtotal + tax;
    return { subtotal, tax, total };
  };

  const handleAddLineItem = () => {
    append({
      description: '',
      quantity: 1,
      rate: client?.chargeRate || 0,
      amount: 0,
    });
  };

  const handleLineItemChange = (index: number, field: keyof InvoiceLineItem, value: any) => {
    const currentItems = watchedLineItems || [];
    const item = { ...currentItems[index] };
    
    if (field === 'quantity' || field === 'rate') {
      item[field] = parseFloat(value) || 0;
      item.amount = item.quantity * item.rate;
    } else if (field === 'description') {
      item.description = value as string;
    } else if (field === 'amount') {
      // Amount is calculated, but handle it if needed
      item.amount = parseFloat(value) || 0;
    }
    
    update(index, item);
  };

  const onSubmit = async (data: InvoiceFormData) => {
    if (!invoice) return;

    try {
      setSaving(true);
      setError(null);

      const totals = calculateTotals(data.lineItems);
      
      const updateData: UpdateInvoiceData = {
        issueDate: data.issueDate,
        dueDate: data.dueDate,
        lineItems: data.lineItems.map(item => ({
          ...item,
          amount: item.quantity * item.rate,
        })),
        subtotal: totals.subtotal,
        tax: totals.tax,
        total: totals.total,
        notes: data.notes || undefined,
        status: data.status,
      };

      await invoicesApi.update(invoice.id, updateData);
      navigate(`/invoices/${invoice.id}`);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save invoice');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error && !invoice) {
    return (
      <Box>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/invoices')}
          sx={{ mb: 2 }}
        >
          Back to Invoices
        </Button>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!invoice) {
    return null;
  }

  const totals = calculateTotals(watchedLineItems || []);

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">
          Edit Invoice {invoice.invoiceNumber}
        </Typography>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/invoices/${invoice.id}`)}
        >
          Cancel
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit(onSubmit)}>
        <Paper sx={{ p: 3, mb: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Issue Date"
                type="date"
                {...register('issueDate', { required: 'Issue date is required' })}
                error={!!errors.issueDate}
                helperText={errors.issueDate?.message}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Due Date"
                type="date"
                {...register('dueDate', { required: 'Due date is required' })}
                error={!!errors.dueDate}
                helperText={errors.dueDate?.message}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            {invoice.status === 'draft' && (
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Controller
                    name="status"
                    control={control}
                    render={({ field }) => (
                      <Select {...field} label="Status">
                        <MenuItem value="draft">Draft</MenuItem>
                        <MenuItem value="sent">Sent</MenuItem>
                      </Select>
                    )}
                  />
                </FormControl>
              </Grid>
            )}
          </Grid>
        </Paper>

        {/* Line Items */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">Line Items</Typography>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddLineItem}
            >
              Add Item
            </Button>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Description</TableCell>
                  <TableCell align="right" sx={{ width: '120px' }}>Quantity</TableCell>
                  <TableCell align="right" sx={{ width: '150px' }}>Rate</TableCell>
                  <TableCell align="right" sx={{ width: '150px' }}>Amount</TableCell>
                  <TableCell align="right" sx={{ width: '80px' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {fields.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        No line items. Click "Add Item" to add one.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  fields.map((field, index) => {
                    const item = watchedLineItems?.[index] || field;
                    return (
                      <TableRow key={field.id}>
                        <TableCell>
                          <TextField
                            fullWidth
                            size="small"
                            value={item.description || ''}
                            onChange={(e) => handleLineItemChange(index, 'description', e.target.value)}
                            placeholder="Item description"
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            fullWidth
                            size="small"
                            type="number"
                            value={item.quantity || 0}
                            onChange={(e) => handleLineItemChange(index, 'quantity', e.target.value)}
                            inputProps={{ min: 0, step: 0.01 }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            fullWidth
                            size="small"
                            type="number"
                            value={item.rate || 0}
                            onChange={(e) => handleLineItemChange(index, 'rate', e.target.value)}
                            inputProps={{ min: 0, step: 0.01 }}
                            InputProps={{
                              startAdornment: (
                                <Typography variant="body2" sx={{ mr: 1 }}>
                                  {invoice.currency || baseCurrency}
                                </Typography>
                              ),
                            }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="medium">
                            {formatCurrency(item.amount || 0, invoice.currency || baseCurrency)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            onClick={() => remove(index)}
                            color="error"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Totals */}
          <Box display="flex" justifyContent="flex-end" mt={3}>
            <Box sx={{ minWidth: '250px' }}>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body1">Subtotal:</Typography>
                <Typography variant="body1">
                  {formatCurrency(totals.subtotal, invoice.currency || baseCurrency)}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body1">Tax:</Typography>
                <Typography variant="body1">
                  {formatCurrency(totals.tax, invoice.currency || baseCurrency)}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" mt={2} pt={2} sx={{ borderTop: '2px solid', borderColor: 'divider' }}>
                <Typography variant="h6" fontWeight="bold">
                  Total:
                </Typography>
                <Typography variant="h6" fontWeight="bold">
                  {formatCurrency(totals.total, invoice.currency || baseCurrency)}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Paper>

        {/* Notes */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Notes
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            {...register('notes')}
            placeholder="Additional notes or terms..."
          />
        </Paper>

        {/* Actions */}
        <Box display="flex" justifyContent="flex-end" gap={2}>
          <Button
            variant="outlined"
            onClick={() => navigate(`/invoices/${invoice.id}`)}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Invoice'}
          </Button>
        </Box>
      </form>
    </Box>
  );
};

export default InvoiceEdit;







